import DashboardBoxTitle from "components/atoms/title/DashboardBoxTitle";
import DashboardBoxLayout from "components/templates/dashboard-box-layout/DashboardBoxLayout";
import { customStylesLightBorder } from "lib/helpers/configs/customStyles";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import Creatable from "react-select/creatable";
import ConditionalRender from "components/atoms/conditional-render/ConditionalRender";
import LoadingScreen from "components/atoms/loading-screen/LoadingScreen";
import PendingsChart from "../AllCharts/PendingsChart";
import { useAppointmentFeedbacks } from "lib/hooks/queries/Statistics/AppointmentFeedbacks/useAppointmentFeedbacks";
import FilterBorderedWrapper from "components/atoms/filter-bordered-wrapper/FilterBorderedWrapper";
import DynamicDropdown from "components/molecules/dynamic-dropdown/DynamicDropdown";
import Filter from "../Dropdowns/Filter";
import Icon from "components/atoms/icon/Icon";
import { IconType } from "lib/helpers/constants/iconTypes";
import { usePermissions } from "lib/hooks/shared/usePermissions";
import { useAllSources } from "lib/hooks/queries/AppointmentSource/useAllSources";
import { useImportSources } from "lib/hooks/queries/AppointmentSource/useImportSources";

export interface IStatisticsProps {
    fetchedUsers: string[]
}

const AppointmentStatistics: FC<IStatisticsProps> = ({ fetchedUsers }) => {


    const { t } = useTranslation();

    const [Filters, setFilters] = useState<{
        worker: string;
        filter_on: string;
        filter_source: number | null;
        filter_type: string | null;
        filter_import_source: string | null;
    }>({
        worker: 'All agents',
        filter_on: 'Today',
        filter_source: null,
        filter_type: 'All',
        filter_import_source: null,
    })
    const hasPermissionToViewAppointmentFeedback = usePermissions(['statistics_appointments_feedback_view']);

    const { data: sourcesData, isLoading: sourcesLoading } = useAllSources();
    const { data: importSourcesData, isLoading: importSourcesLoading } = useImportSources();
    const { data, isLoading } = useAppointmentFeedbacks(Filters);

    const [hoveredSlice, setHoveredSlice] = useState<string | null>(null);

    const [filterTableOpen, setFilterTableOpen] = useState<boolean>(false);


    return (
        <DashboardBoxLayout>
            <div className="flex flex-row gap-3 items-center justify-between mb-5">
                <div className="flex-1">
                    <DashboardBoxTitle
                        title={`Appointments`}
                    />
                </div>

                <div className="flex-none">
                    <div className={`${filterTableOpen && "relative"}`}>
                        <FilterBorderedWrapper
                            className="h-[48px] w-[48px] cursor-pointer"
                            onClick={() => {
                                setFilterTableOpen(!filterTableOpen);
                            }}
                        >
                            <Icon iconType={IconType.FILTER_ICON} />
                        </FilterBorderedWrapper>
                        <ConditionalRender condition={filterTableOpen}>
                            <DynamicDropdown
                                setIsOpen={setFilterTableOpen}
                                showCloseButton={true}
                                title={t('Filter')}
                                className="sm:!w-[350px]"
                            >
                                <Filter
                                    setIsOpen={setFilterTableOpen}
                                    setFilterData={setFilters}
                                    FilterData={Filters}
                                />
                            </DynamicDropdown>
                        </ConditionalRender>
                    </div>
                </div>
            </div>
            <ConditionalRender condition={hasPermissionToViewAppointmentFeedback}>
                <div className="flex flex-col gap-3 items-stretch mb-5 sm:flex-row sm:flex-wrap sm:items-end lg:flex-nowrap lg:items-center">
                    <Creatable<any, false>
                        placeholder={t('Choose Salesman')}
                        options={[
                            ...[
                                { name: t('All agents'), id: 'All agents' },
                                ...fetchedUsers,
                            ].map((user: any) => ({
                                label: user?.name,
                                value: user?.id,
                            })),
                        ]}
                        value={{
                            value: Filters.worker,
                            label: (fetchedUsers as any).find((user: any) => user.id === Filters.worker)?.name || t('All agents'),
                        }}
                        className="cursor-pointer font-inter-regular react-select-container w-full sm:w-[calc(50%-6px)] lg:w-auto lg:flex-1"
                        classNamePrefix="react-select"
                        onChange={(newValue: any) => {
                            setFilters((prevFilters) => ({
                                ...prevFilters,
                                worker: newValue.value,
                            }));
                        }}
                        styles={customStylesLightBorder}
                        isValidNewOption={() => false}
                        menuPosition={"fixed"}
                        menuPortalTarget={document.body}
                    />
                    <Creatable<any, false>
                        placeholder={t('Choose Source')}
                        options={[
                            { label: t('All Sources'), value: null },
                            ...((sourcesData as any)?.sources || []).map((source: any) => ({
                                label: source?.name,
                                value: source?.id,
                            })),
                        ]}
                        value={
                            Filters.filter_source
                                ? {
                                    value: Filters.filter_source,
                                    label: ((sourcesData as any)?.sources || []).find((s: any) => s.id === Filters.filter_source)?.name,
                                }
                                : { value: null, label: t('All Sources') }
                        }
                        className="cursor-pointer font-inter-regular react-select-container w-full sm:w-[calc(50%-6px)] lg:w-auto lg:flex-1"
                        classNamePrefix="react-select"
                        onChange={(newValue: any) => {
                            setFilters((prevFilters) => ({
                                ...prevFilters,
                                filter_source: newValue.value,
                            }));
                        }}
                        styles={customStylesLightBorder}
                        isValidNewOption={() => false}
                        menuPosition={"fixed"}
                        menuPortalTarget={document.body}
                    />
                    <Creatable<any, false>
                        placeholder={t('Import Quelle')}
                        options={[
                            { label: t('All Import Sources'), value: null },
                            ...((importSourcesData as any)?.importSources || []).map((source: string) => ({
                                label: source,
                                value: source,
                            })),
                        ]}
                        value={
                            Filters.filter_import_source
                                ? {
                                    value: Filters.filter_import_source,
                                    label: Filters.filter_import_source,
                                }
                                : { value: null, label: t('All Import Sources') }
                        }
                        className="cursor-pointer font-inter-regular react-select-container w-full sm:w-[calc(50%-6px)] lg:w-auto lg:flex-1"
                        classNamePrefix="react-select"
                        onChange={(newValue: any) => {
                            setFilters((prevFilters) => ({
                                ...prevFilters,
                                filter_import_source: newValue.value,
                            }));
                        }}
                        styles={customStylesLightBorder}
                        isValidNewOption={() => false}
                        menuPosition={"fixed"}
                        menuPortalTarget={document.body}
                    />
                    <Creatable<any, false>
                        placeholder={t('Choose Type')}
                        options={[
                            { label: t('All'), value: 'All' },
                            { label: t('Digital'), value: 'Digital' },
                            { label: t('Physical'), value: 'Physical' },
                        ]}
                        value={{
                            value: Filters.filter_type,
                            label: t(Filters.filter_type || 'All'),
                        }}
                        className="cursor-pointer font-inter-regular react-select-container w-full sm:w-[calc(50%-6px)] lg:w-auto lg:flex-1"
                        classNamePrefix="react-select"
                        onChange={(newValue: any) => {
                            setFilters((prevFilters) => ({
                                ...prevFilters,
                                filter_type: newValue.value,
                            }));
                        }}
                        styles={customStylesLightBorder}
                        isValidNewOption={() => false}
                        menuPosition={"fixed"}
                        menuPortalTarget={document.body}
                    />
                </div>
            </ConditionalRender>


            <div className="flex flex-col gap-20 lg:items-center mb-5 lg:flex-row relative">
                {isLoading ? <div className="py-10"> <LoadingScreen /></div> : (
                    <ConditionalRender condition={data !== undefined}>
                        <div className="basis-1/3">
                            <PendingsChart
                                setHoveredSlice={setHoveredSlice}
                                data={[
                                    { name: 'Abschluss', y: ((data as any)?.data?.won / (data as any)?.data?.total) * 100, z: 100, color: '#88B68B' },
                                    { name: 'Folgetermin', y: ((data as any)?.data?.follow_up / (data as any)?.data?.total) * 100, z: 100, color: '#EDB200' },
                                    { name: 'Kein Abschluss', y: ((data as any)?.data?.lost / (data as any)?.data?.total) * 100, z: 100, color: '#F24822' },
                                    { name: 'Offen', y: ((data as any)?.data?.open / (data as any)?.data?.total) * 100, z: 100, color: '#a6a6a6' },
                                    { name: 'Pendent', y: ((data as any)?.data?.pending / (data as any)?.data?.total) * 100, z: 100, color: '#E6D426' },
                                ]}
                                color={['#88B68B', '#EDB200', '#F24822', '#a6a6a6', '#E6D426']}
                            />
                        </div>
                        <div className="basis-2/3">
                            <div className="grid-cols-2 grid gap-3">
                                {[
                                    { status: 'Abschluss', count: (data as any)?.data?.won, color: '#88B68B' },
                                    { status: 'Folgetermin', count: (data as any)?.data?.follow_up, color: '#EDB200' },
                                    { status: 'Kein Abschluss', count: (data as any)?.data?.lost, color: '#F24822' },
                                    { status: 'Pendent', count: (data as any)?.data?.pending, color: '#E6D426' },
                                    { status: 'Offen', count: (data as any)?.data?.open, color: '#a6a6a6' },
                                    { status: 'Gesamt Termine', count: (data as any)?.data?.total, color: '#2e4aff' },
                                ].map((item: { status: string, count: number, color: string }, index: number) => (
                                    <div
                                        onMouseEnter={() => setHoveredSlice(item.status)}
                                        onMouseLeave={() => setHoveredSlice(null)}
                                        key={index}
                                        className={`flex justify-between gap-3 items-center transition-colors rounded-[8px] py-[10px] px-[20px]
                                            ${item.status === 'Gesamt Termine' ? 'col-span-2' : 'col-span-2 sm:col-span-1'}
                                            ${item.status !== hoveredSlice ? 'text-primaryTextColor border border-[#F2F4F7]' : ''} `
                                        }
                                        style={item.status === hoveredSlice ? { backgroundColor: item.color, borderColor: item.color, color: '#fff' } : {}}
                                    >
                                        <div className="font-inter-medium">
                                            {t(item.status)}
                                        </div>
                                        <div className="font-inter-bold">
                                            {item.count}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </ConditionalRender>
                )}
            </div>
        </DashboardBoxLayout>
    );
};

export default AppointmentStatistics;
